/**
 * Public, requester-facing recap (demo #9). Rendered at /recap/:slug — no auth,
 * no AppShell, opaque slug. The rich "finished story" the recap email links to:
 * the overview, the crew's photos + notes, hours vs. commitment, and a PDF.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUpRight, Download, Loader2, MapPin } from 'lucide-react';
import type { PublicRecapResponse } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { useAuth } from '@/auth/AuthProvider';
import { formatDate } from '@/lib/format';

function fmtMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

function hhmmTo12(hm: string): string {
  const [h = 0, m = 0] = hm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function PublicRecap() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data, isLoading, error } = useQuery<PublicRecapResponse>({
    queryKey: ['public-recap', slug],
    enabled: !!slug,
    queryFn: () => apiCall<PublicRecapResponse>(`/public/recap/${slug}`),
  });

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-6 h-6 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft p-6">
        <div className="max-w-md text-center space-y-3 text-ink">
          <h1 className="text-xl font-semibold">Recap not available</h1>
          <p className="text-sm text-ink-dim">
            This recap isn&rsquo;t ready yet, or the link was revoked or mistyped. Contact your
            Vari project manager for an updated link.
          </p>
        </div>
      </div>
    );
  }

  const photos = data.recap_gallery.filter((a) => a.is_image && a.file_url);
  const openPunch = data.punch_items_total - data.punch_items_closed;

  return (
    <div className="min-h-full bg-fill-soft">
      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {/* Brand bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="font-bold text-lg tracking-tight">VARI · FMS</div>
          {isAuthenticated ? (
            <button
              onClick={() => navigate(`/projects/${data.project_id}`)}
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Open project in app <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <div className="text-[11px] text-ink-faint uppercase tracking-wide">Project recap</div>
          )}
        </div>

        {/* Header */}
        <div className="bg-bg rounded-xl border border-line p-6 shadow-sm space-y-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold leading-tight">{data.title}</h1>
              <p className="text-ink-dim">
                {data.client_name}
                {data.sap_order_number ? ` · Order ${data.sap_order_number}` : ''}
              </p>
            </div>
            {data.recap_pdf_url && (
              <a
                href={data.recap_pdf_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium rounded-lg border border-line px-3 py-1.5 hover:bg-fill-soft"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            )}
          </div>
          {data.completed_at && (
            <div className="inline-flex items-center gap-2 rounded-md bg-status-complete text-status-complete-fg px-2.5 py-1 text-xs font-medium">
              Completed {formatDate(data.completed_at)}
            </div>
          )}
          {data.site_address && (
            <p className="flex items-center gap-1.5 text-sm text-ink-dim pt-1">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {data.site_address.line1}, {data.site_address.city} {data.site_address.state}
            </p>
          )}
        </div>

        {/* Metrics */}
        <div className="bg-bg rounded-xl border border-line p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <Metric n={String(data.tasks.length)} label="Tasks" />
          {data.committed_man_minutes != null ? (
            <Metric
              n={fmtMinutes(data.committed_man_minutes)}
              label="Committed man-hrs"
              sub={
                data.overage_man_minutes > 0
                  ? `+${fmtMinutes(data.overage_man_minutes)} over`
                  : undefined
              }
            />
          ) : (
            <Metric n={fmtMinutes(data.total_crewed_minutes)} label="Man-hours" />
          )}
          <Metric
            n={`${data.punch_items_closed}/${data.punch_items_total}`}
            label="Punch closed"
          />
          <Metric n={String(data.crew_roster.length)} label="Crew" />
        </div>

        {/* Narrative */}
        {data.recap_narrative && (
          <Section title="Summary">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{data.recap_narrative}</p>
          </Section>
        )}

        {/* Photo gallery — the heart of the recap */}
        {photos.length > 0 && (
          <Section title={`Photos · ${photos.length}`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((a) => (
                <a key={a.id} href={a.file_url!} target="_blank" rel="noreferrer">
                  <img
                    src={a.file_url!}
                    alt={a.filename}
                    loading="lazy"
                    className="w-full aspect-square object-cover rounded-lg border border-line hover:opacity-90"
                  />
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* What was done */}
        <Section title="What was done">
          <ul className="space-y-2">
            {data.tasks.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{t.title}</span>
                  {t.sap_rma_reference && (
                    <span className="ml-2 text-xs font-mono text-ink-faint">
                      RMA {t.sap_rma_reference}
                    </span>
                  )}
                </span>
                <span className="text-xs text-ink-faint capitalize whitespace-nowrap">
                  {t.status.replace(/_/g, ' ')}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Crew */}
        {data.crew_roster.length > 0 && (
          <Section title="Crew">
            <div className="flex flex-wrap gap-4">
              {data.crew_roster.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Avatar name={c.display_name} />
                  <div>
                    <div className="font-medium">{c.display_name}</div>
                    <div className="text-xs text-ink-faint">
                      {c.crew_role}
                      {c.is_lead && c.crew_role !== 'lead' && ' · lead'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Working hours */}
        {data.approved_schedule && data.approved_schedule.length > 0 && (
          <Section title="Working hours">
            <ul className="text-sm space-y-1">
              {data.approved_schedule.map((s) => (
                <li key={s.date} className="flex justify-between">
                  <span className="text-ink-dim">{formatDate(s.date)}</span>
                  <span className="font-mono">
                    {hhmmTo12(s.start_time)} – {hhmmTo12(s.end_time)}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {openPunch > 0 && (
          <Section title="Follow-ups">
            <p className="text-sm text-ink-dim">
              {openPunch} punch item{openPunch === 1 ? '' : 's'} remain as a scheduled follow-up.
            </p>
          </Section>
        )}

        <footer className="text-[11px] text-ink-faint text-center pt-3">
          Questions? Reply to the email this link came in, or message your Vari project manager.
        </footer>
      </div>
    </div>
  );
}

function Metric({ n, label, sub }: { n: string; label: string; sub?: string }) {
  return (
    <div>
      <div className="text-xl font-semibold tabular-nums">{n}</div>
      <div className="text-[11px] uppercase tracking-wide text-ink-faint">{label}</div>
      {sub && <div className="text-[11px] text-status-blocked-fg tabular-nums">{sub}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg rounded-xl border border-line p-5">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-3">{title}</div>
      {children}
    </div>
  );
}
